/**
 * The one date-range ladder, shared by every screen that has a range picker.
 *
 * Why this file exists (2026-09-01, Kat). Three screens each hard-coded their
 * own list and they had drifted: the approval queue offered 7 and 14 days, the
 * reviews queue started at 14, and outreach reporting started at 30 — so
 * "how did last week go?" was answerable on one screen and not the other two.
 * Asking the same question of two screens should not require noticing which
 * options someone happened to type there.
 *
 * Each screen keeps its OWN all-time entry, because the three encode it
 * differently in their existing query params (`null`, `0`, and the string
 * "all") and unifying that is a change to three APIs, not to a dropdown.
 * The recency ladder is the part that has to agree, and it is here.
 */

/** Recency options, shortest first. Add one here and every picker gets it. */
export const DATE_WINDOW_DAYS = [7, 14, 30, 90] as const;

export type DateWindowDays = (typeof DATE_WINDOW_DAYS)[number];

/**
 * Which date the window measures. "review" = when the review was written (every
 * picker until 2026-09-30); "reply" = when the owner reply went up, which is the
 * review queue's answer to "what did we reply to this month?".
 */
export type WindowBasis = "review" | "reply";

/**
 * `olderOnly` is the "Older" chip: the same line, the other side of it. The
 * picker has to say so — on the 2026-09-29 Alan Jay call it read "Last 30 days"
 * over a list of 3,611 reviews all MORE than 30 days old. On the reply basis it
 * also says "Replied", so the two dates can't be mistaken for each other.
 */
export function dateWindowLabel(
  days: number,
  olderOnly = false,
  basis: WindowBasis = "review",
): string {
  if (basis === "reply") {
    return olderOnly ? `Replied over ${days} days ago` : `Replied in last ${days} days`;
  }
  return olderOnly ? `Older than ${days} days` : `Last ${days} days`;
}

/** `[{ days, label }]` — the shape the reviews and reporting pickers map over. */
export function dateWindowOptions(
  olderOnly = false,
  basis: WindowBasis = "review",
): { days: number; label: string }[] {
  return DATE_WINDOW_DAYS.map((days) => ({ days, label: dateWindowLabel(days, olderOnly, basis) }));
}

/**
 * Custom dates — Don Elwell (Alan Jay), 2026-10-06, DP #36602: "on the 5th of
 * the month I want to look at what I did" last month, for the gift-card
 * contest. "30 days doesn't work and 60 days is too much."
 *
 * Dates are plain YYYY-MM-DD calendar days in Eastern time, the same days the
 * server counts (lib/review_window.ts parseCalendarRange). "Today" is read in
 * Eastern too, so a manager in another zone still gets the store's month.
 */
export const RANGE_TIMEZONE = "America/New_York";

export type CustomRange = { from: string; to: string };

/** Today's calendar date in Eastern, YYYY-MM-DD. */
export function todayInEastern(now: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: RANGE_TIMEZONE,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(now);
}

/** The whole calendar month before the one `today` (YYYY-MM-DD) is in. */
export function lastFullMonth(today: string): CustomRange {
  const [y, m] = today.split("-").map(Number);
  // Day 0 of this month is the last day of the previous one.
  const last = new Date(Date.UTC(y!, m! - 1, 0));
  const first = new Date(Date.UTC(last.getUTCFullYear(), last.getUTCMonth(), 1));
  return { from: first.toISOString().slice(0, 10), to: last.toISOString().slice(0, 10) };
}

/** The 1st of `today`'s month through `today`. */
export function monthToDate(today: string): CustomRange {
  return { from: `${today.slice(0, 7)}-01`, to: today };
}

/**
 * What's wrong with a range, in words for the person who typed it — or null.
 * The server refuses the same things; checking here just skips a wasted call.
 */
export function rangeProblem(r: CustomRange): string | null {
  const ok = (s: string) => /^\d{4}-\d{2}-\d{2}$/.test(s) && s >= "2000-01-01" && s <= "2100-12-31";
  if (!r.from || !r.to) return "Pick both a start date and an end date.";
  if (!ok(r.from) || !ok(r.to)) return "Those dates don't look right — use the calendar to pick them.";
  if (r.from > r.to) return "The start date is after the end date.";
  return null;
}

/** "Sep 1 – Sep 30, 2026" — what the list is showing, in words. */
export function customRangeLabel(r: CustomRange): string {
  const fmt = (s: string, withYear: boolean) =>
    new Date(`${s}T12:00:00Z`).toLocaleDateString("en-US", {
      timeZone: "UTC",
      month: "short",
      day: "numeric",
      ...(withYear ? { year: "numeric" } : {}),
    });
  const sameYear = r.from.slice(0, 4) === r.to.slice(0, 4);
  return `${fmt(r.from, !sameYear)} – ${fmt(r.to, true)}`;
}
